import { useMemo } from 'react';

import StorePopover from 'components/StorePopover';
import type { StoreIdName } from 'contexts';
import { useSelectStoreChoiceGTM } from 'hooks/gtm';
import type { StoreStockCard } from 'models/api';

interface Props {
	allStoresStock: StoreStockCard[] | undefined;
	changeStoreTitle: string;
	isLoading: boolean;
	isOpen: boolean;

	nearbyStoresStock: StoreStockCard[] | undefined;
	onClose: () => void;
	onUpdateSelectedStore: (store: StoreIdName) => void;
	selectedStore: StoreIdName | undefined;
	selectStoreTitle: string;
}

export default function StockStoreSelectorPopover({
	allStoresStock,
	changeStoreTitle,
	isLoading,
	isOpen,
	nearbyStoresStock,
	onClose,
	onUpdateSelectedStore,
	selectedStore,
	selectStoreTitle,
}: Props) {
	const { sendSelectStoreChoiceEvent } = useSelectStoreChoiceGTM();

	const allStores = useMemo(
		() =>
			allStoresStock?.map((storeStock) => ({
				...storeStock,
				id: storeStock.storeId,
				name: storeStock.storeName,
			})),
		[allStoresStock],
	);
	const nearbyStores = useMemo(
		() =>
			nearbyStoresStock?.map((storeStock) => ({
				...storeStock,
				id: storeStock.storeId,
				name: storeStock.storeName,
			})),
		[nearbyStoresStock],
	);

	return (
		<StorePopover
			baseId="stock-store"
			title={selectedStore?.id ? changeStoreTitle : selectStoreTitle}
			allStores={allStores}
			nearbyStores={nearbyStores}
			selectedStoreId={selectedStore?.id}
			onStoreSelectClick={(store) => {
				onUpdateSelectedStore(store);
				if (
					store.name &&
					'stock' in store &&
					store.stock?.availableStockLevel
				) {
					sendSelectStoreChoiceEvent({
						storeName: store.name,
						storeStock: store.stock.availableStockLevel,
					});
				}

				onClose();
			}}
			onClose={onClose}
			isLoading={isLoading}
			isOpen={isOpen}
		/>
	);
}

StockStoreSelectorPopover.displayName = 'StockStoreSelectorPopover';
